import { formatDates } from '@/lib/utils/date-time/utils';
import { faCheck, faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

const ACTIVE_KEY = 'ACTIVE';
const PENDING_KEY = 'PENDING';

const STATUS_DISPLAY: Record<string, string> = {
  [ACTIVE_KEY]: 'current',
  [PENDING_KEY]: 'future'
};

const FUTURE_KEY = STATUS_DISPLAY[PENDING_KEY];
const CURRENT_KEY = STATUS_DISPLAY[ACTIVE_KEY];

interface Group {
  groupStatus: string;
  [key: string]: any;
}

interface Designation {
  designationStatus?: string;
  firstName?: string;
  lastName?: string;
  reconDueDate?: string;
  lastDataUpdate?: string;
  dataCollectionBegin?: string;
  dataCollectionEnd?: string;
  publishedBegin?: string;
  publishedEnd?: string;
  desigantionLabel?: string;
  version?: string;
  [key: string]: any;
}

interface DesignationData {
  designationInfo?: Designation[];
  quality?: any[];
  costEfficiency?: any[];
  groupAffiliation?: Group[];
  odmDesignationPhase?: any;
  totalCostofCare?: any;
  [key: string]: any;
}

interface SplitResult {
  [FUTURE_KEY]: any;
  [CURRENT_KEY]: any;
}

export default function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Normalizes null/undefined/blank/NaN numeric values (from API, props, or store) to 0
export const toSafeNumber = (value: unknown): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string' && value.trim() === '') return 0;
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(num) ? 0 : num;
};

export function numberWithCommasRounded(x: number | string): string {
  return typeof x === 'number' ? numberWithCommas(Math.round(x)) : '';
}

export const splitCamelCase = (text: string) => {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
};

export function translateDesignation(designationCode: string, translationMap: Record<string, string>): string | null {
  return translationMap[designationCode.toLowerCase().trim()] || null;
}

export function getViewDetailsPath(linkSuffix: string, pathname: string): string {
  if (!pathname.startsWith('/secure/designation')) {
    const pathParts = pathname.split('/');
    const pathPrefix = `/${pathParts[1]}/${pathParts[2]}/${pathParts[3]}/`;
    return `${pathPrefix}${linkSuffix}`;
  }
  return `/secure/${linkSuffix}`;
}

function filterGroups(groups: Group[] = [], designation: Designation = {}): Group[] {
  const { designationStatus = '' } = designation;
  return groups.filter(g => g.groupStatus === designationStatus);
}

function splitSuperPremium(results: any[] = []): SplitResult {
  if (results.length) {
    const reduced = results.reduce((acc: Record<string, any>, item) => {
      const { designationStatus = '' } = item;
      if (designationStatus === PENDING_KEY) acc[FUTURE_KEY] = item;
      else if (designationStatus === ACTIVE_KEY) acc[CURRENT_KEY] = item;
      return acc;
    }, {});
    return {
      [FUTURE_KEY]: reduced[FUTURE_KEY] || null,
      [CURRENT_KEY]: reduced[CURRENT_KEY] || null
    };
  }
  return {
    [FUTURE_KEY]: null,
    [CURRENT_KEY]: null
  };
}

function splitDesignations(designations: Designation[] = []): SplitResult {
  if (designations.length) {
    const reduced = designations.reduce((acc: Record<string, any>, item) => {
      const {
        designationStatus = '',
        firstName = '',
        lastName = '',
        reconDueDate = '',
        lastDataUpdate = '',
        dataCollectionBegin = '',
        dataCollectionEnd = '',
        publishedBegin = '',
        publishedEnd = '',
        desigantionLabel = ''
      } = item;

      const fullName = `${firstName} ${lastName}`;

      const formattedDates = formatDates({
        reconDueDate,
        lastDataUpdate,
        dataCollectionBegin,
        dataCollectionEnd,
        publishedBegin,
        publishedEnd
      });

      const shared = {
        ...item,
        ...formattedDates,
        fullName,
        designationLabel: desigantionLabel
      };

      if (designationStatus === PENDING_KEY) {
        acc[FUTURE_KEY] = {
          ...shared,
          selectValue: FUTURE_KEY
        };
      } else if (designationStatus === ACTIVE_KEY) {
        acc[CURRENT_KEY] = {
          ...shared,
          selectValue: CURRENT_KEY
        };
      }

      return acc;
    }, {});

    return {
      [FUTURE_KEY]: reduced[FUTURE_KEY] || null,
      [CURRENT_KEY]: reduced[CURRENT_KEY] || null
    };
  }
  return {
    [FUTURE_KEY]: null,
    [CURRENT_KEY]: null
  };
}

export function getDesignationsWithData(data: DesignationData): SplitResult {
  const {
    designationInfo = [],
    quality = [],
    costEfficiency = [],
    groupAffiliation = [],
    odmDesignationPhase,
    totalCostofCare
  } = data;

  const { current, future } = splitDesignations(designationInfo);
  const spQuality = splitSuperPremium(quality);
  const spCost = splitSuperPremium(costEfficiency);
  const spTotal = totalCostofCare;

  return {
    [FUTURE_KEY]: future
      ? {
        ...future,
        odmDesignationPhase,
        spQuality: spQuality[FUTURE_KEY],
        spCost: spCost[FUTURE_KEY],
        spTotal: totalCostofCare,
        groups: filterGroups(groupAffiliation, future)
      }
      : null,
    [CURRENT_KEY]: current
      ? {
        ...current,
        odmDesignationPhase,
        spQuality: spQuality[CURRENT_KEY],
        spCost: spCost[CURRENT_KEY],
        spTotal: spTotal,
        groups: filterGroups(groupAffiliation, current)
      }
      : null
  };
}

interface SelectOption {
  value: string;
  label: string;
}

export function buildSelectOptions(designations: Record<string, Designation> = {}): SelectOption[] {
  return Object.keys(designations)
    .filter(key => designations[key])
    .map(key => {
      const { version = '', publishedBegin = '' } = designations[key];
      return {
        value: key,
        label: `Version ${version} - ${publishedBegin}`
      };
    });
};

export function getEvaluationDetails(result: string) {
  console.log(result);
  switch (result) {
    case 'Meets Criteria':
      return {
        text: 'Meets Criteria',
        className: '!text-[#007000]', // green
        icon: faCheck,
      };
    case 'Does Not Meet Criteria':
      return {
        text: 'Does Not Meet Criteria',
        className: '!text-[#d32f2f]', // red
        icon: faTimes,
      };
    case 'Not Evaluated':
      return {
        className: '!text-black',
        text: 'Not Evaluated',
        icon: faTriangleExclamation
      };
    default:
      return {
        className: '!invisible',
        icon: faTriangleExclamation,
        text: '',
      };
  }
};



export const buildDesignationPhysicianLinks = (mpin: string, designation: Designation) => {
  const defaultLinks = [
    { label: 'Overview', to: `/secure/physician/${mpin}/designation/overview/` },
    { label: 'Quality of Care', to: `/secure/physician/${mpin}/designation/quality-measures/` },
  ];
  const reconLink = { label: 'Reconsiderations', to: `/secure/physician/${mpin}/designation/case-details/` };
  const patientTotalCostLink = { label: 'Efficiency of Care', to: `/secure/physician/${mpin}/designation/patient-total-cost/` };
  const patientEpisodeLink = { label: 'Efficiency of Care', to: `/secure/physician/${mpin}/designation/patient-episode-cost/` };
  const linkWithEpisodes =
    // eslint-disable-next-line no-nested-ternary
    designation && designation.showPopulationCost === '1'
      ? designation.populationCostOutcomeCD === 'ID'
        ? [...defaultLinks, reconLink]
        : [...defaultLinks, patientTotalCostLink, reconLink]
      : designation.episodeCostOutcomeCD === 'ID'
        ? [...defaultLinks, reconLink]
        : [...defaultLinks, patientEpisodeLink, reconLink];
  const newLinks = designation ? linkWithEpisodes : defaultLinks;
  return newLinks.map((link, idx) => ({
    ...link,
    value: (idx + 1).toString(),
  }));
};

export const buildDesignationOverviewLinks = (designation: Designation) => {
  const defaultLinks = [
    { label: 'Overview', to: `/secure/designation/overview/` },
    { label: 'Quality of Care', to: `/secure/designation/quality-measures/` },
  ];
  const reconLink = { label: 'Reconsiderations', to: `/secure/designation/case-details/` };
  const patientTotalCostLink = { label: 'Efficiency of Care', to: `/secure/designation/patient-total-cost/` };
  const patientEpisodeLink = { label: 'Efficiency of Care', to: `/secure/designation/patient-episode-cost/` };
  const linkWithEpisodes =
    // eslint-disable-next-line no-nested-ternary
    designation && designation.showPopulationCost === '1'
      ? designation.populationCostOutcomeCD === 'ID'
        ? [...defaultLinks, reconLink]
        : [...defaultLinks, patientTotalCostLink, reconLink]
      : designation.episodeCostOutcomeCD === 'ID'
        ? [...defaultLinks, reconLink]
        : [...defaultLinks, patientEpisodeLink, reconLink];
  const newLinks = designation ? linkWithEpisodes : defaultLinks;
  return newLinks.map((link, idx) => ({
    ...link,
    value: (idx + 1).toString(),
  }));
};
